import { useEffect, useState } from "react";
import { devError } from "@/lib/log";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusPill, VendeIALogo } from "@/components/Brand";
import {
  Gear,
  Buildings,
  Users,
  ShieldCheck,
  Storefront,
  WhatsappLogo,
  Check,
  Minus,
  Info,
  Plus,
  Trash,
  PencilSimple,
  X,
  FloppyDisk,
  Copy,
  ClockClockwise,
  PaperPlaneTilt,
  Bell,
} from "@phosphor-icons/react";
import Sucursales from "@/pages/Sucursales";
import Usuarios from "@/pages/Usuarios";

const TABS = [
  { id: "general", label: "General", icon: Storefront, help: "Ajustes globales del negocio: nombre, moneda, WhatsApp, URL pública." },
  { id: "sucursales", label: "Sucursales", icon: Buildings, help: "Crea y administra los puntos de venta del negocio." },
  { id: "usuarios", label: "Usuarios", icon: Users, help: "Da de alta cajeros y empleados, asigna rol y sucursal." },
  { id: "permisos", label: "Roles & Permisos", icon: ShieldCheck, help: "Qué puede hacer cada tipo de usuario en el sistema. Crea roles personalizados con permisos a la medida." },
];

const PERMISSIONS = [
  { key: "ver_inventario", label: "Ver inventario" },
  { key: "editar_inventario", label: "Crear / editar productos" },
  { key: "escanear_ai", label: "Usar AI Scanner (foto + voz)" },
  { key: "publicaciones", label: "Generar publicaciones / posts" },
  { key: "ventas_ver", label: "Ver pedidos y ventas" },
  { key: "ventas_crear", label: "Registrar ventas y cobros" },
  { key: "reportes_propios", label: "Ver corte del día propio" },
  { key: "reportes_globales", label: "Reportes globales / comparativas entre sucursales" },
  { key: "exportar_datos", label: "Exportar / importar Excel" },
  { key: "gestionar_sucursales", label: "Crear / editar sucursales" },
  { key: "gestionar_usuarios", label: "Crear / editar usuarios y roles" },
  { key: "ajustes_globales", label: "Modificar ajustes generales del negocio" },
];

const VALUES = ["all", "own", "none"];

const VALUE_LABELS = {
  all: { text: "Total", className: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  own: { text: "Su sucursal", className: "text-amber-700 bg-amber-50 border-amber-200" },
  none: { text: "No", className: "text-zinc-500 bg-zinc-50 border-zinc-200" },
};

// Predefined templates: pre-filled permission matrices to accelerate role creation.
// Each template fills a `permissions` map for ALL keys in PERMISSIONS.
const TEMPLATE_DEFS = [
  {
    id: "readonly",
    label: "Solo lectura",
    descripcion: "Ve inventario, ventas y reportes propios. No modifica nada.",
    permissions: {
      ver_inventario: "all",
      editar_inventario: "none",
      escanear_ai: "none",
      publicaciones: "none",
      ventas_ver: "own",
      ventas_crear: "none",
      reportes_propios: "all",
      reportes_globales: "none",
      exportar_datos: "none",
      gestionar_sucursales: "none",
      gestionar_usuarios: "none",
      ajustes_globales: "none",
    },
  },
  {
    id: "ventas",
    label: "Solo ventas",
    descripcion: "Cobra, ve inventario y registra ventas. No edita inventario ni reportes globales.",
    permissions: {
      ver_inventario: "all",
      editar_inventario: "none",
      escanear_ai: "none",
      publicaciones: "none",
      ventas_ver: "own",
      ventas_crear: "own",
      reportes_propios: "all",
      reportes_globales: "none",
      exportar_datos: "none",
      gestionar_sucursales: "none",
      gestionar_usuarios: "none",
      ajustes_globales: "none",
    },
  },
  {
    id: "encargado",
    label: "Encargado de sucursal",
    descripcion: "Hace todo en su sucursal: inventario, AI, posts, ventas y reportes propios.",
    permissions: {
      ver_inventario: "own",
      editar_inventario: "own",
      escanear_ai: "all",
      publicaciones: "own",
      ventas_ver: "own",
      ventas_crear: "own",
      reportes_propios: "all",
      reportes_globales: "none",
      exportar_datos: "own",
      gestionar_sucursales: "none",
      gestionar_usuarios: "none",
      ajustes_globales: "none",
    },
  },
  {
    id: "vacio",
    label: "Vacío",
    descripcion: "Restablece todos los permisos a 'No'.",
    permissions: Object.fromEntries(PERMISSIONS.map((p) => [p.key, "none"])),
  },
];

export default function Ajustes() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "general";

  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const setTab = (id) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-8 max-w-[1400px] mx-auto" data-testid="ajustes-page">
      <header className="mb-6 flex items-start gap-4">
        <Gear size={32} className="text-klein shrink-0 mt-1" weight="duotone" />
        <div>
          <div className="overline text-klein">Configuración del sistema</div>
          <h1 className="h-display text-4xl sm:text-5xl font-black tracking-tighter">Ajustes</h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Todo lo que define cómo opera tu negocio: datos generales, sucursales, usuarios y los privilegios de cada rol.
          </p>
        </div>
      </header>

      <nav className="border-b border-zinc-200 mb-8 flex flex-wrap gap-x-1 gap-y-2 -mx-1" data-testid="ajustes-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`ajustes-tab-${t.id}`}
              data-help={t.help}
              className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors ${
                active
                  ? "border-klein text-klein font-medium bg-klein/[0.03]"
                  : "border-transparent text-muted2 hover:text-ink hover:bg-zinc-50"
              }`}
            >
              <t.icon size={16} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "general" && <GeneralTab />}
      {tab === "sucursales" && (
        <div data-testid="ajustes-tab-content-sucursales"><Sucursales embedded /></div>
      )}
      {tab === "usuarios" && (
        <div data-testid="ajustes-tab-content-usuarios"><Usuarios embedded /></div>
      )}
      {tab === "permisos" && <PermisosTab />}
    </div>
  );
}

function GeneralTab() {
  const [s, setS] = useState({
    whatsapp_numero: "",
    nombre_negocio: "VendeIA",
    moneda: "MXN",
    public_url_base: "",
    descuento_referido_pct: 10,
    bank_titular: "",
    bank_nombre: "",
    bank_clabe: "",
    bank_cuenta: "",
    bank_concepto: "",
    social_instagram: "",
    social_facebook: "",
    social_tiktok: "",
    social_marketplace: "",
    responsable_nombre: "",
    responsable_whatsapp: "",
    autopilot_enabled: false,
    autopilot_interval_hours: 24,
    autopilot_score_threshold: 20,
    autopilot_max_per_run: 3,
    telegram_bot_token: "",
    telegram_chat_id: "",
    telegram_notify_autopilot: true,
    telegram_notify_leads: true,
    telegram_notify_conversations: true,
    telegram_notify_weekly_spam_digest: true,
    telegram_notify_crm: true,
    crm_en_riesgo_days: 60,
    crm_inactivo_days: 120,
    crm_vip_threshold: 5000,
    ai_agent_enabled: true,
    ai_followup_enabled: true,
    ai_followup_discount_24h: 0,
    ai_followup_discount_48h: 5,
    ai_followup_discount_72h: 10,
  });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/settings/health");
      setHealth(data);
    } catch (e) {
      devError(e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setS({
          whatsapp_numero: data.whatsapp_numero || "",
          nombre_negocio: data.nombre_negocio || "VendeIA",
          moneda: data.moneda || "MXN",
          public_url_base:
            data.public_url_base || (typeof window !== "undefined" ? window.location.origin : ""),
          descuento_referido_pct: data.descuento_referido_pct ?? 10,
          bank_titular: data.bank_titular || "",
          bank_nombre: data.bank_nombre || "",
          bank_clabe: data.bank_clabe || "",
          bank_cuenta: data.bank_cuenta || "",
          bank_concepto: data.bank_concepto || "",
          social_instagram: data.social_instagram || "",
          social_facebook: data.social_facebook || "",
          social_tiktok: data.social_tiktok || "",
          social_marketplace: data.social_marketplace || "",
          responsable_nombre: data.responsable_nombre || "",
          responsable_whatsapp: data.responsable_whatsapp || "",
          autopilot_enabled: !!data.autopilot_enabled,
          autopilot_interval_hours: data.autopilot_interval_hours ?? 24,
          autopilot_score_threshold: data.autopilot_score_threshold ?? 20,
          autopilot_max_per_run: data.autopilot_max_per_run ?? 3,
          telegram_bot_token: data.telegram_bot_token || "",
          telegram_chat_id: data.telegram_chat_id || "",
          telegram_notify_autopilot: data.telegram_notify_autopilot ?? true,
          telegram_notify_leads: data.telegram_notify_leads ?? true,
          telegram_notify_conversations: data.telegram_notify_conversations ?? true,
          telegram_notify_weekly_spam_digest: data.telegram_notify_weekly_spam_digest ?? true,
          telegram_notify_crm: data.telegram_notify_crm ?? true,
          crm_en_riesgo_days: data.crm_en_riesgo_days ?? 60,
          crm_inactivo_days: data.crm_inactivo_days ?? 120,
          crm_vip_threshold: data.crm_vip_threshold ?? 5000,
          ai_agent_enabled: data.ai_agent_enabled ?? true,
          ai_followup_enabled: data.ai_followup_enabled ?? true,
          ai_followup_discount_24h: data.ai_followup_discount_24h ?? 0,
          ai_followup_discount_48h: data.ai_followup_discount_48h ?? 5,
          ai_followup_discount_72h: data.ai_followup_discount_72h ?? 10,
        });
        await loadHealth();
      } catch (e) {
        devError("load settings error", e);
      }
    })();
  }, []);

  const save = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setLoading(true);
    try {
      await api.put("/settings", s);
      toast.success("Ajustes guardados");
      await loadHealth();
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo guardar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="general-form">
      {health && <PaymentHealthPanel health={health} />}
      <section className="border border-zinc-200 p-6">
        <div className="text-sm font-medium mb-1">Datos del negocio</div>
        <div className="text-xs text-muted2 mb-5">Aparecerán en tus páginas públicas de producto y links de WhatsApp.</div>
        <div className="space-y-4">
          <div>
            <label className="overline block mb-1.5"><Storefront size={11} className="inline mr-1" /> Nombre del negocio</label>
            <input className="input-flat" value={s.nombre_negocio} onChange={(e) => setS({ ...s, nombre_negocio: e.target.value })} data-testid="setting-name" data-help="Nombre comercial visible para los compradores y en mensajes de WhatsApp." />
          </div>
          <div>
            <label className="overline block mb-1.5"><WhatsappLogo size={11} className="inline mr-1 text-[#25D366]" /> WhatsApp (formato internacional)</label>
            <input className="input-flat" placeholder="5215512345678" value={s.whatsapp_numero} onChange={(e) => setS({ ...s, whatsapp_numero: e.target.value.replace(/[^0-9+]/g, "") })} data-testid="setting-wa" data-help="Solo dígitos con lada de país. Ej.: 5215512345678 para México." />
            <div className="text-[11px] text-muted2 mt-1">Sin espacios ni guiones. Incluye lada del país (ej. 521… para México).</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Moneda</label>
              <select className="input-flat" value={s.moneda} onChange={(e) => setS({ ...s, moneda: e.target.value })} data-testid="setting-currency" data-help="Moneda en la que se expresan precios, ingresos y reportes.">
                {["MXN", "USD", "EUR", "ARS", "COP", "CLP", "PEN"].map((c) => (<option key={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Descuento referido (%)</label>
              <input type="number" min="0" max="50" className="input-flat" value={s.descuento_referido_pct} onChange={(e) => setS({ ...s, descuento_referido_pct: Number(e.target.value) || 0 })} data-testid="setting-discount" data-help="Porcentaje de descuento que se otorga al usar un código de referido." />
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">URL base pública</label>
            <input className="input-flat font-mono text-xs" value={s.public_url_base} onChange={(e) => setS({ ...s, public_url_base: e.target.value })} data-testid="setting-url" data-help="Dominio donde se publican los productos. Se usa para generar links compartibles." />
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 p-6" data-testid="settings-bank-section">
        <div className="text-sm font-medium mb-1">Cuenta bancaria · Transferencia</div>
        <div className="text-xs text-muted2 mb-5">
          Si llenas estos datos, los compradores verán un botón <strong>Pagar por transferencia</strong> en la página pública con esta información. Déjalo vacío para ocultar la opción.
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Titular de la cuenta</label>
              <input className="input-flat" value={s.bank_titular} onChange={(e) => setS({ ...s, bank_titular: e.target.value })} data-testid="setting-bank-titular" placeholder="Ej. María López" />
            </div>
            <div>
              <label className="overline block mb-1.5">Banco</label>
              <input className="input-flat" value={s.bank_nombre} onChange={(e) => setS({ ...s, bank_nombre: e.target.value })} data-testid="setting-bank-name" placeholder="Ej. BBVA, Santander, Banorte" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">CLABE interbancaria</label>
              <input className="input-flat font-mono" value={s.bank_clabe} onChange={(e) => setS({ ...s, bank_clabe: e.target.value.replace(/[^0-9]/g, "").slice(0, 18) })} data-testid="setting-bank-clabe" placeholder="012180012345678901" />
              <div className="text-[11px] text-muted2 mt-1">18 dígitos. La CLABE permite transferencias entre bancos.</div>
            </div>
            <div>
              <label className="overline block mb-1.5">Número de cuenta (opcional)</label>
              <input className="input-flat font-mono" value={s.bank_cuenta} onChange={(e) => setS({ ...s, bank_cuenta: e.target.value.replace(/[^0-9]/g, "").slice(0, 20) })} data-testid="setting-bank-cuenta" placeholder="1234567890" />
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">Concepto sugerido (opcional)</label>
            <input className="input-flat" value={s.bank_concepto} onChange={(e) => setS({ ...s, bank_concepto: e.target.value })} data-testid="setting-bank-concepto" placeholder="Ej. Pedido VendeIA" />
            <div className="text-[11px] text-muted2 mt-1">Texto que sugerimos al comprador como concepto al transferir.</div>
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 p-6" data-testid="settings-responsable-section">
        <div className="text-sm font-medium mb-1">Responsable de seguimiento</div>
        <div className="text-xs text-muted2 mb-5">
          Cuando un comprador pique el botón de WhatsApp, queda registrado un lead pendiente para esta persona (visible en <strong>Dashboard › Avisos</strong>).
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1.5">Nombre del responsable</label>
            <input className="input-flat" value={s.responsable_nombre} onChange={(e) => setS({ ...s, responsable_nombre: e.target.value })} data-testid="setting-resp-name" placeholder="Ej. Carlos Pérez" />
          </div>
          <div>
            <label className="overline block mb-1.5">WhatsApp del responsable</label>
            <input className="input-flat font-mono" value={s.responsable_whatsapp} onChange={(e) => setS({ ...s, responsable_whatsapp: e.target.value.replace(/[^0-9+]/g, "") })} data-testid="setting-resp-wa" placeholder="5215512345678" />
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 p-6" data-testid="settings-social-section">
        <div className="text-sm font-medium mb-1">Redes sociales</div>
        <div className="text-xs text-muted2 mb-5">
          Aparecerán como botones en la página pública del producto. Puedes poner el usuario (@miNegocio) o el link completo.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1.5">Instagram</label>
            <input className="input-flat" value={s.social_instagram} onChange={(e) => setS({ ...s, social_instagram: e.target.value })} data-testid="setting-social-instagram" placeholder="@minegocio o https://instagram.com/minegocio" />
          </div>
          <div>
            <label className="overline block mb-1.5">Facebook</label>
            <input className="input-flat" value={s.social_facebook} onChange={(e) => setS({ ...s, social_facebook: e.target.value })} data-testid="setting-social-facebook" placeholder="minegocio o https://facebook.com/minegocio" />
          </div>
          <div>
            <label className="overline block mb-1.5">TikTok</label>
            <input className="input-flat" value={s.social_tiktok} onChange={(e) => setS({ ...s, social_tiktok: e.target.value })} data-testid="setting-social-tiktok" placeholder="@minegocio" />
          </div>
          <div>
            <label className="overline block mb-1.5">Marketplace / Tienda externa</label>
            <input className="input-flat" value={s.social_marketplace} onChange={(e) => setS({ ...s, social_marketplace: e.target.value })} data-testid="setting-social-marketplace" placeholder="https://marketplace.facebook.com/profile/…" />
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 p-6" data-testid="settings-ai-agent-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-medium mb-1">Agente IA en conversaciones</div>
            <div className="text-xs text-muted2">
              Chat público en cada producto. La IA responde preguntas, captura datos del comprador y manda seguimientos automáticos si el cliente no responde — con descuento progresivo (24 h / 48 h / 72 h).
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={!!s.ai_agent_enabled}
              onChange={(e) => setS({ ...s, ai_agent_enabled: e.target.checked })}
              data-testid="ai-agent-toggle"
              className="h-4 w-4 accent-klein"
            />
            <span className={`text-xs font-medium ${s.ai_agent_enabled ? "text-[#1B5E20]" : "text-muted2"}`}>
              {s.ai_agent_enabled ? "Activo" : "Inactivo"}
            </span>
          </label>
        </div>
        {s.ai_agent_enabled && (
          <div className="space-y-4 border-t border-zinc-100 pt-4 mt-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.ai_followup_enabled}
                onChange={(e) => setS({ ...s, ai_followup_enabled: e.target.checked })}
                data-testid="ai-followup-toggle"
                className="h-4 w-4 accent-klein"
              />
              <span className="text-xs font-medium">
                Activar seguimientos automáticos si el comprador no responde
              </span>
            </label>
            {s.ai_followup_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="overline block mb-1.5">Descuento a las 24 h (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    className="input-flat"
                    value={s.ai_followup_discount_24h}
                    onChange={(e) => setS({ ...s, ai_followup_discount_24h: Number(e.target.value) || 0 })}
                    data-testid="ai-discount-24h"
                  />
                  <div className="text-[11px] text-muted2 mt-1">0% = sólo recordatorio suave.</div>
                </div>
                <div>
                  <label className="overline block mb-1.5">Descuento a las 48 h (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    className="input-flat"
                    value={s.ai_followup_discount_48h}
                    onChange={(e) => setS({ ...s, ai_followup_discount_48h: Number(e.target.value) || 0 })}
                    data-testid="ai-discount-48h"
                  />
                  <div className="text-[11px] text-muted2 mt-1">Recomendado 5%.</div>
                </div>
                <div>
                  <label className="overline block mb-1.5">Descuento a las 72 h (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    className="input-flat"
                    value={s.ai_followup_discount_72h}
                    onChange={(e) => setS({ ...s, ai_followup_discount_72h: Number(e.target.value) || 0 })}
                    data-testid="ai-discount-72h"
                  />
                  <div className="text-[11px] text-muted2 mt-1">Última llamada. 10% recomendado.</div>
                </div>
              </div>
            )}
            <div className="text-[11px] text-muted2 bg-zinc-50 border border-zinc-200 px-3 py-2">
              💡 La IA detecta alta intención de compra y te avisa por Telegram al instante. Puedes responder desde la app o desde Telegram con <code className="px-1 bg-white">/r &lt;id&gt; tu mensaje</code>.
            </div>
          </div>
        )}
      </section>

      <section className="border border-zinc-200 p-6" data-testid="settings-autopilot-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-medium mb-1">Piloto automático con IA</div>
            <div className="text-xs text-muted2">
              Mejora periódicamente los posts con score muy bajo sin que tengas que hacer nada. Trabaja en segundo plano.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={!!s.autopilot_enabled}
              onChange={(e) => setS({ ...s, autopilot_enabled: e.target.checked })}
              data-testid="autopilot-toggle"
              className="h-4 w-4 accent-klein"
            />
            <span className={`text-xs font-medium ${s.autopilot_enabled ? "text-[#1B5E20]" : "text-muted2"}`}>
              {s.autopilot_enabled ? "Activo" : "Inactivo"}
            </span>
          </label>
        </div>
        {s.autopilot_enabled && (
          <div className="space-y-4 border-t border-zinc-100 pt-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="overline block mb-1.5">Cada cuántas horas correr</label>
                <select
                  value={s.autopilot_interval_hours}
                  onChange={(e) => setS({ ...s, autopilot_interval_hours: Number(e.target.value) })}
                  className="input-flat"
                  data-testid="autopilot-interval"
                >
                  <option value={6}>Cada 6 h (4 veces/día)</option>
                  <option value={12}>Cada 12 h (2 veces/día)</option>
                  <option value={24}>Cada 24 h (1 vez/día)</option>
                  <option value={48}>Cada 48 h</option>
                  <option value={168}>Cada semana</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Mejorar si score ≤</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="input-flat"
                  value={s.autopilot_score_threshold}
                  onChange={(e) => setS({ ...s, autopilot_score_threshold: Number(e.target.value) || 0 })}
                  data-testid="autopilot-threshold"
                />
                <div className="text-[11px] text-muted2 mt-1">Score 0-100. Recomendado 20.</div>
              </div>
              <div>
                <label className="overline block mb-1.5">Máximo posts por corrida</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="input-flat"
                  value={s.autopilot_max_per_run}
                  onChange={(e) => setS({ ...s, autopilot_max_per_run: Number(e.target.value) || 1 })}
                  data-testid="autopilot-max"
                />
                <div className="text-[11px] text-muted2 mt-1">Cap para no quemar tu llave de IA.</div>
              </div>
            </div>
            <div className="text-[11px] text-muted2 bg-zinc-50 border border-zinc-200 px-3 py-2">
              ⚡ Tip: empieza con 1 vez/día y 2 posts/corrida. El piloto guarda historial completo — siempre puedes revertir desde Publicaciones.
            </div>
          </div>
        )}
      </section>

      <TelegramSection
        token={s.telegram_bot_token}
        chatId={s.telegram_chat_id}
        notifyAutopilot={s.telegram_notify_autopilot}
        notifyLeads={s.telegram_notify_leads}
        notifyConversations={s.telegram_notify_conversations}
        notifyWeeklyDigest={s.telegram_notify_weekly_spam_digest}
        notifyCrm={s.telegram_notify_crm}
        onChange={(patch) => setS({ ...s, ...patch })}
        onPersist={save}
      />

      <BrandingSection />


      <div className="flex justify-end">
        <button type="submit" disabled={loading} className="bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="setting-save" data-help="Guarda los cambios. Aplican al instante en toda la plataforma.">
          {loading ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </form>
  );
}

function PaymentHealthPanel({ health }) {
  const channels = [
    { key: "stripe", label: "Pago con tarjeta (Stripe)", hint: "Cobros con tarjeta a tu cuenta de Stripe.", platform: "vendeia" },
    { key: "whatsapp", label: "WhatsApp del negocio", hint: "Botón verde en la página pública.", platform: "whatsapp" },
    { key: "bank", label: "Transferencia bancaria", hint: "Datos visibles al comprador para transferir.", platform: "vendeia" },
    { key: "responsable", label: "Responsable de seguimiento", hint: "Recibe los leads de WhatsApp.", platform: "vendeia" },
    { key: "socials", label: "Redes sociales", hint: "Links Instagram / FB / TikTok en la página.", platform: "instagram" },
  ];
  const okCount = channels.filter((c) => health[c.key]?.ready).length;
  return (
    <section
      className="border border-zinc-200 p-6"
      data-testid="payment-health-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">Estado de cobros y publicaciones</div>
          <div className="text-sm font-medium mt-0.5">
            <span className="text-[#1B5E20] font-bold">{okCount}</span>/{channels.length} canales listos
          </div>
        </div>
        <StatusPill
          status={okCount === channels.length ? "connected" : okCount === 0 ? "needs_config" : "configured"}
          label={okCount === channels.length ? "Todo listo" : okCount === 0 ? "Configura para vender" : "Parcialmente listo"}
        />
      </div>
      <ul className="divide-y divide-zinc-100">
        {channels.map((c) => {
          const ready = !!health[c.key]?.ready;
          const reason = health[c.key]?.reason || "";
          return (
            <li
              key={c.key}
              className="flex items-start gap-3 py-3"
              data-testid={`health-${c.key}-${ready ? "ok" : "fail"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium">{c.label}</div>
                  <StatusPill
                    status={ready ? "connected" : "needs_config"}
                    label={ready ? "Listo" : "Falta"}
                    size="sm"
                  />
                </div>
                <div className="text-xs text-muted2 mt-1">
                  {ready ? c.hint : reason || "Sin configurar"}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="text-[11px] text-muted2 mt-3 border-t pt-2 leading-relaxed">
        ⚠️ Los botones marcados como "Falta" <strong>no aparecen</strong> en la página pública del producto — así evitas que un comprador pique un botón roto.
      </div>
    </section>
  );
}

function BrandingSection() {
  const [hide, setHide] = useState(() => {
    try {
      return localStorage.getItem("hide_emergent_badge") === "1";
    } catch {
      return false;
    }
  });

  const togglePref = (value) => {
    setHide(value);
    try {
      localStorage.setItem("hide_emergent_badge", value ? "1" : "0");
      window.dispatchEvent(new Event("vendeia:badge-pref-changed"));
      toast.success(value ? "Sello «Made with Emergent» oculto" : "Sello visible nuevamente");
    } catch {
      toast.error("No se pudo guardar la preferencia");
    }
  };

  return (
    <section
      className="border border-zinc-200 p-6"
      data-testid="settings-branding-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium mb-1 flex items-center gap-2 flex-wrap">
            <VendeIALogo size={16} withText={false} />
            Marca & sello
            <StatusPill
              status={hide ? "disabled" : "connected"}
              label={hide ? "Sello oculto" : "Sello visible"}
              size="sm"
            />
          </div>
          <div className="text-xs text-muted2 max-w-xl">
            Controla si el botón flotante "<strong>Made with Emergent</strong>" aparece en
            tu sitio público. Mantenerlo visible apoya a la plataforma; ocultarlo
            te da una imagen 100 % blanca para clientes y socios.
          </div>
        </div>
      </div>

      <label
        className="flex items-start gap-3 cursor-pointer mt-4 px-3 py-3 bg-zinc-50 border border-zinc-200"
        data-help="Cuando se desactiva, el sello de Emergent desaparece en preview y en producción."
      >
        <input
          type="checkbox"
          checked={hide}
          onChange={(e) => togglePref(e.target.checked)}
          className="mt-0.5 accent-klein w-4 h-4"
          data-testid="toggle-hide-emergent-badge"
        />
        <div className="text-xs">
          <div className="font-semibold text-ink">
            Ocultar sello "Made with Emergent"
          </div>
          <div className="text-muted2 mt-0.5">
            Cuando esté activo, el botón flotante en la esquina inferior no se mostrará para visitantes ni para ti.
          </div>
        </div>
      </label>

      <div className="mt-3 px-3 py-2 bg-[#FFF8E1] border border-[#FFCC80] text-[11px] text-[#7C5807] leading-relaxed">
        ℹ️ La preferencia se guarda en este navegador (localStorage). Si abres el sitio en otro dispositivo, ajústalo ahí también.
      </div>
    </section>
  );
}

function TelegramSection({ token, chatId, notifyAutopilot, notifyLeads, notifyConversations, notifyWeeklyDigest, notifyCrm, onChange, onPersist }) {
  const [validating, setValidating] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [botInfo, setBotInfo] = useState(null);

  const validateToken = async () => {
    if (!token?.trim()) {
      toast.error("Pega tu token primero");
      return;
    }
    setValidating(true);
    try {
      const { data } = await api.post("/telegram/test", { token: token.trim() });
      setBotInfo(data);
      toast.success(`Bot detectado: @${data.bot_username}`);
    } catch (e) {
      setBotInfo(null);
      toast.error(formatApiError(e, "Token inválido"));
    } finally {
      setValidating(false);
    }
  };

  const detectChat = async () => {
    setDetecting(true);
    try {
      // Make sure the token is persisted server-side before we ask Telegram
      // who messaged it most recently.
      await onPersist();
      const { data } = await api.post("/telegram/detect-chat");
      onChange({ telegram_chat_id: data.chat_id });
      toast.success(`Chat detectado (${data.chat_name}) — revisa Telegram`);
    } catch (e) {
      toast.error(formatApiError(e, "No pude detectar el chat"));
    } finally {
      setDetecting(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/telegram/send-test");
      toast.success("Mensaje de prueba enviado");
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo enviar"));
    } finally {
      setTesting(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/spam/digest/send-now");
      if (data?.sent) {
        toast.success("Resumen anti-spam enviado a Telegram");
      } else {
        toast.warning("No se envió. Verifica que tengas token y chat ID configurados.");
      }
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo enviar el resumen"));
    } finally {
      setSendingDigest(false);
    }
  };

  const connected = !!token && !!chatId;
  const status = !token ? "needs_config" : !chatId ? "needs_config" : "connected";

  return (
    <section className="border border-zinc-200 p-6" data-testid="settings-telegram-section">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium mb-1 flex items-center gap-2 flex-wrap">
            <PaperPlaneTilt size={14} className="text-[#0088CC]" />
            Notificaciones por Telegram
            <StatusPill
              status={status}
              label={!token ? "Falta token" : !chatId ? "Falta chat ID" : "Conectado"}
              size="sm"
            />
          </div>
          <div className="text-xs text-muted2">
            Recibe el resumen del piloto automático y avisos de leads pendientes en tu chat de Telegram.
          </div>
        </div>
      </div>

      <details className="text-xs border border-zinc-100 bg-zinc-50 px-3 py-2 mb-4" data-testid="telegram-setup-guide">
        <summary className="cursor-pointer font-medium text-ink">¿Cómo configurar? (2 minutos)</summary>
        <ol className="mt-2 space-y-1 list-decimal list-inside text-muted2">
          <li>En Telegram, busca <b>@BotFather</b> y envía <code className="font-mono">/newbot</code>.</li>
          <li>Sigue las instrucciones — al final te dará un token tipo <code className="font-mono">1234567890:AAH...</code>.</li>
          <li>Pégalo abajo y toca <b>Verificar bot</b>.</li>
          <li>Abre tu bot (link que te dio BotFather) y mándale <b>"hola"</b>.</li>
          <li>Toca <b>Detectar mi chat</b> — listo, te llegará un mensaje de confirmación.</li>
        </ol>
      </details>

      <div className="space-y-4">
        <div>
          <label className="overline block mb-1.5">Bot token</label>
          <div className="flex gap-2">
            <input
              type="password"
              className="input-flat flex-1 font-mono text-xs"
              value={token}
              onChange={(e) => {
                onChange({ telegram_bot_token: e.target.value });
                setBotInfo(null);
              }}
              placeholder="1234567890:AAH..."
              data-testid="telegram-token-input"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={validateToken}
              disabled={validating || !token}
              className="btn-ghost"
              data-testid="telegram-validate"
            >
              {validating ? "Verificando…" : "Verificar bot"}
            </button>
          </div>
          {botInfo && (
            <div className="text-[11px] text-[#1B5E20] mt-1.5">
              ✓ Bot verificado: <span className="font-mono">@{botInfo.bot_username}</span>
            </div>
          )}
        </div>

        <div>
          <label className="overline block mb-1.5">Chat ID</label>
          <div className="flex gap-2">
            <input
              className="input-flat flex-1 font-mono text-xs"
              value={chatId}
              onChange={(e) => onChange({ telegram_chat_id: e.target.value.replace(/[^0-9-]/g, "") })}
              placeholder="Se detecta automáticamente"
              data-testid="telegram-chat-input"
              readOnly={!chatId}
            />
            <button
              type="button"
              onClick={detectChat}
              disabled={detecting || !token}
              className="btn-ghost whitespace-nowrap"
              data-testid="telegram-detect"
              title="Asegúrate de mandarle 'hola' a tu bot antes de tocar esto"
            >
              {detecting ? "Detectando…" : "Detectar mi chat"}
            </button>
          </div>
          <div className="text-[11px] text-muted2 mt-1">
            Antes de tocar Detectar: abre tu bot en Telegram y mándale cualquier mensaje.
          </div>
        </div>

        {connected && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-zinc-100 pt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!notifyAutopilot}
                  onChange={(e) => onChange({ telegram_notify_autopilot: e.target.checked })}
                  className="mt-0.5 accent-klein"
                  data-testid="telegram-notify-autopilot"
                />
                <div className="text-xs">
                  <div className="font-medium">Resumen del piloto automático</div>
                  <div className="text-muted2">Te avisa qué posts mejoró y el Δ de score.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!notifyLeads}
                  onChange={(e) => onChange({ telegram_notify_leads: e.target.checked })}
                  className="mt-0.5 accent-klein"
                  data-testid="telegram-notify-leads"
                />
                <div className="text-xs">
                  <div className="font-medium">Leads de WhatsApp pendientes</div>
                  <div className="text-muted2">Aviso al llegar a 5, 10, 20… sin atender.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!notifyConversations}
                  onChange={(e) => onChange({ telegram_notify_conversations: e.target.checked })}
                  className="mt-0.5 accent-klein"
                  data-testid="telegram-notify-conversations"
                />
                <div className="text-xs">
                  <div className="font-medium">Conversaciones IA</div>
                  <div className="text-muted2">Nuevas charlas + alta intención de compra.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!notifyWeeklyDigest}
                  onChange={(e) => onChange({ telegram_notify_weekly_spam_digest: e.target.checked })}
                  className="mt-0.5 accent-klein"
                  data-testid="telegram-notify-weekly-spam-digest"
                />
                <div className="text-xs">
                  <div className="font-medium">Resumen anti-spam semanal</div>
                  <div className="text-muted2">Lunes por la mañana: bloqueados, tokens ahorrados, top spammers.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!notifyCrm}
                  onChange={(e) => onChange({ telegram_notify_crm: e.target.checked })}
                  className="mt-0.5 accent-klein"
                  data-testid="telegram-notify-crm"
                />
                <div className="text-xs">
                  <div className="font-medium">CRM · VIPs en riesgo</div>
                  <div className="text-muted2">Aviso diario de clientes fieles que llevan +60 días sin volver.</div>
                </div>
              </label>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={sendTest}
                disabled={testing}
                className="btn-ghost text-xs"
                data-testid="telegram-send-test"
              >
                <Bell size={12} weight="fill" /> {testing ? "Enviando…" : "Enviar mensaje de prueba"}
              </button>
              <button
                type="button"
                onClick={sendDigest}
                disabled={sendingDigest}
                className="btn-ghost text-xs"
                data-testid="telegram-send-spam-digest"
                title="Envía el resumen anti-spam de los últimos 7 días ahora mismo (ignora el cooldown semanal)."
              >
                🛡️ {sendingDigest ? "Enviando…" : "Enviar resumen anti-spam ahora"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}


function PermisosTab() {
  const [roles, setRoles] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const viewHistory = (role) => {
    const params = new URLSearchParams({
      entity: "roles",
      entity_id: role.key,
      label: role.label || role.key,
    });
    navigate(`/auditoria?${params.toString()}`);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (role) => {
    setEditingKey(role.key);
    setDraft({
      key: role.key,
      label: role.label,
      descripcion: role.descripcion || "",
      permissions: { ...role.permissions },
      builtin: role.builtin,
    });
  };

  const startNew = () => {
    setEditingKey("__new__");
    const empty = {};
    PERMISSIONS.forEach((p) => (empty[p.key] = "none"));
    setDraft({ key: "", label: "", descripcion: "", permissions: empty, builtin: false });
  };

  const startClone = (role) => {
    setEditingKey("__new__");
    setDraft({
      key: "",
      label: `Copia de ${role.label}`,
      descripcion: role.descripcion || "",
      permissions: { ...role.permissions },
      builtin: false,
    });
    // Scroll to the new row so user sees the form
    setTimeout(() => {
      document.querySelector('[data-testid="role-row-new"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const cancel = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const setPerm = (k, v) => setDraft((d) => ({ ...d, permissions: { ...d.permissions, [k]: v } }));

  const applyTemplate = (tplId) => {
    const tpl = TEMPLATE_DEFS.find((t) => t.id === tplId);
    if (!tpl || !draft) return;
    const next = {};
    PERMISSIONS.forEach((p) => {
      // Lock admin's critical perms regardless of template
      if (editingKey === "admin" && ["gestionar_usuarios", "gestionar_sucursales", "ajustes_globales"].includes(p.key)) {
        next[p.key] = "all";
      } else {
        next[p.key] = tpl.permissions[p.key] ?? "none";
      }
    });
    setDraft((d) => ({
      ...d,
      permissions: next,
      // Pre-fill description for new roles only when blank
      descripcion: editingKey === "__new__" && !d.descripcion ? tpl.descripcion : d.descripcion,
      label: editingKey === "__new__" && !d.label ? tpl.label : d.label,
    }));
    toast.success(`Plantilla '${tpl.label}' aplicada`);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      if (editingKey === "__new__") {
        if (!/^[a-z][a-z0-9_]{1,30}$/.test(draft.key)) {
          throw new Error("El identificador debe tener solo minúsculas, números o _ (2-31 caracteres)");
        }
        if (!draft.label.trim()) throw new Error("Falta el nombre del rol");
        await api.post("/roles", {
          key: draft.key,
          label: draft.label.trim(),
          descripcion: draft.descripcion.trim(),
          permissions: draft.permissions,
        });
        toast.success("Rol creado");
      } else {
        await api.put(`/roles/${editingKey}`, {
          label: draft.label.trim(),
          descripcion: draft.descripcion.trim(),
          permissions: draft.permissions,
        });
        toast.success("Rol actualizado");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response ? formatApiError(e) : e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`¿Eliminar rol "${role.label}"?`)) return;
    try {
      await api.delete(`/roles/${role.key}`);
      toast.success("Rol eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="permisos-tab">
      <section>
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-sm font-medium">Roles del negocio</div>
            <div className="text-xs text-muted2 mt-1">
              Cada usuario tiene un rol que define qué puede hacer. Los roles del sistema (admin, cajero, empleado) no se pueden eliminar pero sí editar parcialmente.
            </div>
          </div>
          <button
            onClick={startNew}
            data-testid="role-new-btn"
            data-help="Crea un rol personalizado con la mezcla exacta de permisos que necesites (ej. 'Encargado de sucursal')."
            className="bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90 inline-flex items-center gap-2"
          >
            <Plus size={14} /> Nuevo rol
          </button>
        </div>

        {draft && (
          <TemplateBar
            onApply={applyTemplate}
            mode={editingKey === "__new__" ? "new" : "edit"}
            roleLabel={draft.label || (editingKey === "__new__" ? "nuevo rol" : "rol")}
          />
        )}

        <div className="border border-zinc-200 hidden lg:block overflow-x-auto" data-testid="roles-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-64">Rol</th>
                {PERMISSIONS.map((p) => (
                  <th key={p.key} className="text-left px-2 py-3 font-medium text-xs whitespace-normal" title={p.label}>
                    <div className="max-w-[110px] leading-tight">{p.label}</div>
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-medium w-32">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => {
                const isEditing = editingKey === r.key && draft;
                return (
                  <tr key={r.key} className="border-t border-zinc-100 align-top" data-testid={`role-row-${r.key}`}>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input className="input-flat text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Nombre" data-testid="role-edit-label" />
                          <textarea className="input-flat text-xs min-h-[50px]" value={draft.descripcion} onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })} placeholder="Descripción" data-testid="role-edit-descripcion" />
                          <div className="font-mono text-[10px] text-muted2">{r.key}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {r.label}
                            {r.builtin && (
                              <span className="text-[10px] uppercase tracking-wider bg-zinc-100 px-1.5 py-0.5 text-zinc-600 rounded">sistema</span>
                            )}
                          </div>
                          <div className="text-xs text-muted2 mt-1 max-w-xs">{r.descripcion}</div>
                          <div className="font-mono text-[10px] text-muted2 mt-1">{r.key}</div>
                        </div>
                      )}
                    </td>
                    {PERMISSIONS.map((p) => {
                      const val = isEditing ? draft.permissions[p.key] : r.permissions[p.key];
                      const lock = isEditing && r.key === "admin" && ["gestionar_usuarios", "gestionar_sucursales", "ajustes_globales"].includes(p.key);
                      return (
                        <td key={p.key} className="px-2 py-3" data-testid={`perm-${r.key}-${p.key}`}>
                          {isEditing ? (
                            <select
                              value={val}
                              onChange={(e) => setPerm(p.key, e.target.value)}
                              disabled={lock}
                              data-testid={`perm-edit-${r.key}-${p.key}`}
                              className={`text-xs px-1.5 py-1 border bg-white disabled:bg-zinc-50 disabled:cursor-not-allowed ${VALUE_LABELS[val]?.className || ""}`}
                            >
                              {VALUES.map((v) => (
                                <option key={v} value={v}>{VALUE_LABELS[v].text}</option>
                              ))}
                            </select>
                          ) : (
                            <PermPill value={val} />
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={save} disabled={saving} data-testid={`role-save-${r.key}`} data-help="Guarda los cambios al rol y aplican de inmediato a todos sus usuarios." className="bg-klein text-white px-3 py-1 text-xs hover:bg-klein/90 disabled:opacity-60 inline-flex items-center gap-1">
                            <FloppyDisk size={12} /> Guardar
                          </button>
                          <button onClick={cancel} className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => viewHistory(r)} data-testid={`role-history-${r.key}`} data-help="Ver historial de cambios de este rol en la auditoría." className="border border-zinc-300 px-2.5 py-1 text-xs hover:bg-zinc-50 inline-flex items-center gap-1">
                            <ClockClockwise size={12} />
                          </button>
                          <button onClick={() => startEdit(r)} data-testid={`role-edit-${r.key}`} data-help="Edita los permisos y descripción de este rol." className="border border-zinc-300 px-2.5 py-1 text-xs hover:bg-zinc-50 inline-flex items-center gap-1">
                            <PencilSimple size={12} />
                          </button>
                          <button onClick={() => startClone(r)} data-testid={`role-clone-${r.key}`} data-help="Crea un rol nuevo basado en este, con los mismos permisos. Útil para variantes (ej: 'Encargado Norte', 'Encargado Sur')." className="border border-zinc-300 px-2.5 py-1 text-xs hover:bg-zinc-50 inline-flex items-center gap-1">
                            <Copy size={12} />
                          </button>
                          {!r.builtin && (
                            <button onClick={() => remove(r)} data-testid={`role-delete-${r.key}`} data-help="Elimina el rol. Solo puedes hacerlo si nadie lo tiene asignado." className="border border-red-200 text-red-600 px-2.5 py-1 text-xs hover:bg-red-50 inline-flex items-center gap-1">
                              <Trash size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {editingKey === "__new__" && draft && (
                <tr className="border-t border-klein/40 bg-klein/[0.04] align-top" data-testid="role-row-new">
                  <td className="px-4 py-3">
                    <div className="space-y-2">
                      <input className="input-flat text-sm font-mono" value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase() })} placeholder="identificador (ej: encargado)" data-testid="role-new-key" />
                      <input className="input-flat text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Nombre visible" data-testid="role-new-label" />
                      <textarea className="input-flat text-xs min-h-[50px]" value={draft.descripcion} onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })} placeholder="Descripción" data-testid="role-new-descripcion" />
                    </div>
                  </td>
                  {PERMISSIONS.map((p) => {
                    const val = draft.permissions[p.key];
                    return (
                      <td key={p.key} className="px-2 py-3">
                        <select value={val} onChange={(e) => setPerm(p.key, e.target.value)} data-testid={`perm-new-${p.key}`} className={`text-xs px-1.5 py-1 border bg-white ${VALUE_LABELS[val]?.className || ""}`}>
                          {VALUES.map((v) => (<option key={v} value={v}>{VALUE_LABELS[v].text}</option>))}
                        </select>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={save} disabled={saving} data-testid="role-save-new" className="bg-klein text-white px-3 py-1 text-xs hover:bg-klein/90 disabled:opacity-60 inline-flex items-center gap-1">
                        <FloppyDisk size={12} /> Crear
                      </button>
                      <button onClick={cancel} className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50">
                        <X size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-muted2 leading-relaxed border-l-2 border-klein pl-3 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0 text-klein" />
          <div>
            <strong className="text-emerald-700">Total</strong>: ve y modifica todo el negocio.
            <strong className="text-amber-700 ml-2">Su sucursal</strong>: limitado a la sucursal asignada y sus propias ventas.
            <strong className="text-zinc-500 ml-2">No</strong>: sin acceso. <br />
            Los cambios se aplican inmediatamente a todos los usuarios del rol. El rol <strong>admin</strong> no puede perder los permisos críticos para evitar que el sistema se bloquee.
          </div>
        </div>

        {/* Mobile / tablet portrait: card view of roles */}
        <div className="lg:hidden space-y-3 mt-4" data-testid="roles-cards">
          {roles.map((r) => {
            const isEditing = editingKey === r.key && draft;
            return (
              <div key={r.key} className="border border-zinc-200" data-testid={`role-card-${r.key}`}>
                <div className="px-4 py-3 border-b border-zinc-100 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <input className="input-flat text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                    ) : (
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {r.label}
                        {r.builtin && (
                          <span className="text-[10px] uppercase tracking-wider bg-zinc-100 px-1.5 py-0.5 text-zinc-600 rounded">sistema</span>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-muted2 mt-1">{r.descripcion}</div>
                    <div className="font-mono text-[10px] text-muted2 mt-0.5">{r.key}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <button onClick={save} disabled={saving} className="bg-klein text-white px-3 py-1.5 text-xs hover:bg-klein/90 disabled:opacity-60 inline-flex items-center gap-1">
                          <FloppyDisk size={12} />
                        </button>
                        <button onClick={cancel} className="border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50">
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(r)} className="border border-zinc-300 px-2.5 py-1.5 text-xs hover:bg-zinc-50">
                          <PencilSimple size={12} />
                        </button>
                        <button onClick={() => startClone(r)} className="border border-zinc-300 px-2.5 py-1.5 text-xs hover:bg-zinc-50">
                          <Copy size={12} />
                        </button>
                        {!r.builtin && (
                          <button onClick={() => remove(r)} className="border border-red-200 text-red-600 px-2.5 py-1.5 text-xs hover:bg-red-50">
                            <Trash size={12} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <ul className="divide-y divide-zinc-100">
                  {PERMISSIONS.map((p) => {
                    const val = isEditing ? draft.permissions[p.key] : r.permissions[p.key];
                    const lock = isEditing && r.key === "admin" && ["gestionar_usuarios", "gestionar_sucursales", "ajustes_globales"].includes(p.key);
                    return (
                      <li key={p.key} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="flex-1 min-w-0 leading-tight">{p.label}</span>
                        {isEditing ? (
                          <select
                            value={val}
                            onChange={(e) => setPerm(p.key, e.target.value)}
                            disabled={lock}
                            className={`text-xs px-2 py-1 border bg-white disabled:bg-zinc-50 ${VALUE_LABELS[val]?.className || ""}`}
                          >
                            {VALUES.map((v) => (
                              <option key={v} value={v}>{VALUE_LABELS[v].text}</option>
                            ))}
                          </select>
                        ) : (
                          <PermPill value={val} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          {editingKey === "__new__" && draft && (
            <div className="border border-klein/40 bg-klein/[0.04]" data-testid="role-card-new">
              <div className="px-4 py-3 border-b border-klein/30 space-y-2">
                <input className="input-flat text-sm font-mono" value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase() })} placeholder="identificador" />
                <input className="input-flat text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Nombre visible" />
                <textarea className="input-flat text-xs min-h-[50px]" value={draft.descripcion} onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })} placeholder="Descripción" />
                <div className="flex gap-2 pt-1">
                  <button onClick={save} disabled={saving} className="bg-klein text-white px-3 py-1.5 text-xs hover:bg-klein/90 disabled:opacity-60 inline-flex items-center gap-1 flex-1 justify-center">
                    <FloppyDisk size={12} /> Crear
                  </button>
                  <button onClick={cancel} className="border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50">
                    <X size={12} />
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100">
                {PERMISSIONS.map((p) => {
                  const val = draft.permissions[p.key];
                  return (
                    <li key={p.key} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                      <span className="flex-1 min-w-0 leading-tight">{p.label}</span>
                      <select value={val} onChange={(e) => setPerm(p.key, e.target.value)} className={`text-xs px-2 py-1 border bg-white ${VALUE_LABELS[val]?.className || ""}`}>
                        {VALUES.map((v) => (<option key={v} value={v}>{VALUE_LABELS[v].text}</option>))}
                      </select>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PermPill({ value }) {
  const cfg = VALUE_LABELS[value] || VALUE_LABELS.none;
  const Icon = value === "none" ? Minus : Check;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border ${cfg.className}`}>
      <Icon size={10} weight="bold" />
      {cfg.text}
    </span>
  );
}

function TemplateBar({ onApply, mode, roleLabel }) {
  return (
    <div
      className="border border-klein/30 bg-klein/[0.04] p-3 mb-3 flex items-center flex-wrap gap-2"
      data-testid="role-template-bar"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-klein font-medium flex items-center gap-1.5 mr-1">
        <ShieldCheck size={12} weight="fill" />
        Plantillas
      </div>
      <span className="text-[11px] text-muted2 mr-1">
        {mode === "new" ? "para acelerar el alta de" : "aplicar a"} <strong>{roleLabel}</strong>:
      </span>
      {TEMPLATE_DEFS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onApply(t.id)}
          title={t.descripcion}
          data-testid={`role-template-${t.id}`}
          data-help={t.descripcion}
          className="text-xs px-2.5 py-1 border border-klein/40 bg-white text-klein hover:bg-klein hover:text-white transition-colors"
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
